import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

import { OperationalModel } from '§M/types/enumerated.type'

const SESSION_STORAGE_KEY = 'turing_selected_tool'

const isOperationalModel = (value: unknown): value is OperationalModel =>
  value === OperationalModel.AXIOS || value === OperationalModel.NEXUS

/**
 * Holds the tool (operational model) the user picked for this session.
 *
 * Projects, locations and concepts are scoped to it, so it is cleared whenever the
 * organization changes — module subscriptions differ per organization.
 */
export const useToolStore = defineStore('tool', () => {
  const selectedTool = ref<OperationalModel | null>(null)

  const hasTool = computed(() => selectedTool.value !== null)

  const setTool = (tool: OperationalModel): void => {
    selectedTool.value = tool
    try {
      sessionStorage.setItem(SESSION_STORAGE_KEY, tool)
    } catch (error) {
      console.warn('Failed to persist tool to sessionStorage:', error)
    }
  }

  const clearTool = (): void => {
    selectedTool.value = null
    try {
      sessionStorage.removeItem(SESSION_STORAGE_KEY)
    } catch (error) {
      console.warn('Failed to clear tool from sessionStorage:', error)
    }
  }

  /**
   * Restore the tool from sessionStorage on app start.
   * Returns true if a tool was restored.
   */
  const initializeFromSession = (): boolean => {
    try {
      const stored = sessionStorage.getItem(SESSION_STORAGE_KEY)
      if (isOperationalModel(stored)) {
        selectedTool.value = stored
        return true
      }
    } catch (error) {
      console.warn('Failed to restore tool from sessionStorage:', error)
    }
    return false
  }

  return {
    selectedTool,
    hasTool,
    setTool,
    clearTool,
    initializeFromSession
  }
})
